import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  FaTimes, FaArrowLeft, FaSitemap, FaCopy, FaCheck, FaSpinner, FaPen,
  FaLaptop, FaMobileAlt, FaTabletAlt, FaExclamationTriangle, FaUserLock,
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { fechaLocal, formatFecha, iniciales } from './vacantesShared';
import './VacantesUI.css';
import './ExpedienteEmpleado.css';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];

const antiguedad = (iso) => {
  const inicio = fechaLocal(iso);
  if (!inicio) return null;
  const hoy = new Date();
  let meses = (hoy.getFullYear() - inicio.getFullYear()) * 12 + (hoy.getMonth() - inicio.getMonth());
  if (hoy.getDate() < inicio.getDate()) meses -= 1;
  if (meses < 1) return 'Menos de un mes';
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  const partes = [];
  if (anios) partes.push(anios === 1 ? '1 año' : `${anios} años`);
  if (resto) partes.push(resto === 1 ? '1 mes' : `${resto} meses`);
  return partes.join(' y ');
};

const cumpleanos = (iso) => {
  const f = fechaLocal(iso);
  return f ? `${f.getDate()} de ${MESES[f.getMonth()]}` : null;
};

const capital = (texto) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1).replace(/_/g, ' ') : '');

const ICONO_EQUIPO = { asset: FaLaptop, smartphone: FaMobileAlt, tablet: FaTabletAlt };

const Seccion = ({ titulo, extra, children }) => (
  <section className="exp-seccion">
    <div className="exp-seccion-cabecera">
      <h3>{titulo}</h3>
      {extra}
    </div>
    {children}
  </section>
);

const Vacio = ({ children }) => <p className="exp-vacio">{children}</p>;

/**
 * Expediente de una persona: datos, puesto, trayectoria, equipos, tickets y
 * tareas. Se puede saltar a su jefe o a quienes le reportan sin cerrar el panel.
 * Vacaciones, permisos y constancias quedan fuera: son asunto de GH.
 */
function ExpedienteEmpleado({ empleadoId, onClose, onCambio, onCerrarCuentas }) {
  const [pila, setPila] = useState([empleadoId]);
  const actualId = pila[pila.length - 1];
  const [exp, setExp] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [verHistorialEquipos, setVerHistorialEquipos] = useState(false);
  const [editandoDetalles, setEditandoDetalles] = useState(false);
  const [detalles, setDetalles] = useState('');
  const [copiado, setCopiado] = useState(false);

  const cargar = useCallback(async (id) => {
    setCargando(true);
    setError(null);
    try {
      const r = await axiosInstance.get(`/api/empleados/${id}/expediente`);
      setExp(r.data);
      setDetalles(r.data.puesto?.detalles_cargo || '');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el expediente');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar(actualId);
    setVerHistorialEquipos(false);
    setEditandoDetalles(false);
  }, [actualId, cargar]);

  useEffect(() => {
    const alEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', alEscape);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alEscape);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onClose]);

  const irA = (id) => setPila((p) => [...p, id]);
  const volver = () => setPila((p) => p.slice(0, -1));

  const persona = exp?.persona;
  const puesto = exp?.puesto;

  const verOrganigrama = () => {
    const params = new URLSearchParams({
      gerencia: puesto?.gerencia_id || '',
      departamento: puesto?.departamento_id || '',
      area: puesto?.area_id || '',
    });
    window.open(`/organigrama?${params.toString()}`, '_blank');
  };

  const copiarContacto = async () => {
    const lineas = [
      persona.nombre,
      [puesto?.cargo, puesto?.detalles_cargo].filter(Boolean).join(', '),
      persona.correo,
      persona.extension && `Ext. ${persona.extension}`,
      persona.linea && `Línea ${persona.linea}`,
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lineas.join('\n'));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      // sin permiso de portapapeles
    }
  };

  const guardarDetalles = async () => {
    try {
      await axiosInstance.patch(`/api/empleados/${persona.id}`, { detalles_cargo: detalles });
      setExp((e) => ({ ...e, puesto: { ...e.puesto, detalles_cargo: detalles } }));
      setEditandoDetalles(false);
      onCambio?.();
    } catch {
      alert('No se pudieron guardar los detalles del cargo');
    }
  };

  const contenido = () => {
    if (cargando && !exp) {
      return <div className="exp-cargando"><FaSpinner className="animate-spin" /> Cargando expediente...</div>;
    }
    if (error) {
      return <div className="form-modal-error m-6"><FaExclamationTriangle /> {error}</div>;
    }
    const { trayectoria, equipos, tickets, tareas } = exp;
    const ruta = [puesto?.gerencia, puesto?.departamento, puesto?.area].filter(Boolean).join(' › ');

    return (
      <div className={`exp-cuerpo${cargando ? ' exp-cuerpo--recargando' : ''}`}>
        <header className="exp-cabecera">
          {persona.foto
            ? <img className="exp-foto" src={persona.foto} alt="" />
            : <span className="exp-foto exp-foto--iniciales">{iniciales(persona.nombre)}</span>}
          <div className="min-w-0 flex-1">
            <h2 className="exp-nombre">{persona.nombre}</h2>
            <p className="exp-cargo">
              {puesto?.cargo || 'Sin cargo'}
              {puesto?.detalles_cargo ? `, ${puesto.detalles_cargo}` : ''}
            </p>
            <p className="exp-ruta">{ruta}</p>
            <div className="exp-chips">
              {!persona.activo && <span className="vac-estado vac-estado--cancelada">Egresado</span>}
              {persona.ficha_ti_pendiente && <span className="vac-estado vac-estado--abierta">Ficha TI pendiente</span>}
              {exp.de_vacaciones && <span className="vac-estado vac-estado--en-proceso">De vacaciones</span>}
            </div>
          </div>
        </header>

        <div className="exp-acciones">
          <button type="button" className="btn btn-secondary btn-sm" onClick={verOrganigrama}>
            <FaSitemap /> Ver en organigrama
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={copiarContacto}>
            {copiado ? <><FaCheck /> Copiado</> : <><FaCopy /> Copiar contacto</>}
          </button>
          {/* TI: lo que le quedó abierto al egresar (lo registró GH o algo falló) */}
          {!persona.activo && onCerrarCuentas && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onCerrarCuentas(persona.id)}>
              <FaUserLock /> Cerrar cuentas
            </button>
          )}
        </div>

        <dl className="exp-datos">
          {[
            ['Ficha', persona.ficha],
            ['Cédula', persona.cedula],
            ['Antigüedad', antiguedad(persona.fecha_ingreso)],
            ['Ingreso', formatFecha(persona.fecha_ingreso)],
            ['Cumpleaños', cumpleanos(persona.fecha_nacimiento)],
            ['Sede', puesto?.sede],
            ['Correo', persona.correo],
            ['Extensión', persona.extension],
            ['Línea', persona.linea],
          ].map(([etiqueta, valor]) => (
            <div key={etiqueta}>
              <dt>{etiqueta}</dt>
              <dd>{valor || <span className="text-gray-400">Sin dato</span>}</dd>
            </div>
          ))}
        </dl>

        {puesto && (
          <Seccion titulo="Puesto">
            <div className="exp-fila">
              <span className="exp-fila-etiqueta">Detalles del cargo</span>
              {editandoDetalles ? (
                <span className="flex items-center gap-2 flex-1">
                  <input
                    className="input py-1"
                    value={detalles}
                    onChange={(e) => setDetalles(e.target.value)}
                    placeholder="Ej: Centro Occidente"
                    autoFocus
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={guardarDetalles}>Guardar</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditandoDetalles(false); setDetalles(puesto.detalles_cargo || ''); }}>Cancelar</button>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {puesto.detalles_cargo || <span className="text-gray-400">Sin detalles</span>}
                  <button type="button" className="exp-icono-btn" onClick={() => setEditandoDetalles(true)} aria-label="Editar detalles del cargo">
                    <FaPen />
                  </button>
                </span>
              )}
            </div>
            <div className="exp-fila">
              <span className="exp-fila-etiqueta">Reporta a</span>
              {puesto.supervisor ? (
                puesto.supervisor.es_vacante
                  ? <span className="exp-persona exp-persona--vacante">{puesto.supervisor.nombre}</span>
                  : <button type="button" className="exp-persona" onClick={() => irA(puesto.supervisor.id)}>{puesto.supervisor.nombre}</button>
              ) : <span className="text-gray-400">Nadie</span>}
            </div>
            <div className="exp-fila exp-fila--arriba">
              <span className="exp-fila-etiqueta">Le reportan</span>
              {puesto.reportes.length ? (
                <span className="exp-personas">
                  {puesto.reportes.map((r) => (r.tipo === 'vacante'
                    ? <span key={`v${r.id}`} className="exp-persona exp-persona--vacante" title="Puesto vacante">{r.nombre}</span>
                    : (
                      <button type="button" key={r.id} className="exp-persona" onClick={() => irA(r.id)} title={r.cargo || ''}>
                        {r.nombre}
                      </button>
                    )))}
                </span>
              ) : <span className="text-gray-400">Nadie</span>}
            </div>
          </Seccion>
        )}

        {trayectoria && (
          <Seccion titulo="Trayectoria">
            <ol className="exp-linea">
              {trayectoria.map((h, i) => (
                <li key={i} className={`exp-hito exp-hito--${h.tipo}`}>
                  <span className="exp-hito-fecha">{formatFecha(h.fecha) || 'Sin fecha'}</span>
                  <span className="exp-hito-titulo">{h.titulo}</span>
                  {h.detalle && <span className="exp-hito-detalle">{h.detalle}</span>}
                </li>
              ))}
            </ol>
          </Seccion>
        )}

        {equipos && (
          <Seccion
            titulo="Equipos"
            extra={equipos.historial.length > 0 && (
              <button type="button" className="vac-link-btn" onClick={() => setVerHistorialEquipos((v) => !v)}>
                {verHistorialEquipos ? 'Ocultar historial' : 'Ver historial'}
              </button>
            )}
          >
            {equipos.actuales.length ? (
              <ul className="exp-lista">
                {equipos.actuales.map((e) => {
                  const Icono = ICONO_EQUIPO[e.tipo] || FaLaptop;
                  return (
                    <li key={`${e.tipo}${e.id}`}>
                      <Icono className="text-gray-400 flex-shrink-0" />
                      <span className="flex-1">{e.descripcion}{e.nombre_equipo ? ` (${e.nombre_equipo})` : ''}</span>
                      {e.activo_fijo && <span className="exp-meta">AF {e.activo_fijo}</span>}
                    </li>
                  );
                })}
              </ul>
            ) : <Vacio>No tiene equipos asignados.</Vacio>}
            {verHistorialEquipos && (
              <ul className="exp-lista exp-lista--tenue">
                {equipos.historial.map((h, i) => (
                  <li key={i}>
                    <span className="exp-meta w-20 flex-shrink-0">{formatFecha(h.fecha)}</span>
                    <span className="flex-1">{capital(h.accion)}: {h.detalle}</span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
        )}

        {tickets && (
          <Seccion
            titulo="Tickets de soporte"
            extra={tickets.cerrados > 0 && <span className="exp-meta">{tickets.cerrados} cerrados</span>}
          >
            {!tickets.tiene_usuario ? <Vacio>No tiene usuario de Centro Sura.</Vacio>
              : tickets.abiertos.length ? (
                <ul className="exp-lista">
                  {tickets.abiertos.map((t) => (
                    <li key={t.id}>
                      <span className="exp-meta w-20 flex-shrink-0">{formatFecha(t.fecha)}</span>
                      <span className="flex-1">{t.titulo}</span>
                      <span className="exp-meta">{capital(t.estado)}</span>
                    </li>
                  ))}
                </ul>
              ) : <Vacio>No tiene tickets abiertos.</Vacio>}
          </Seccion>
        )}

        {tareas && (
          <Seccion titulo="Proyectos">
            {tareas.activas ? (
              <p className="m-0 text-sm text-gray-700">
                {tareas.activas === 1 ? '1 tarea activa' : `${tareas.activas} tareas activas`}
                {tareas.atrasadas > 0 && <span className="text-red-600 font-medium">, {tareas.atrasadas === 1 ? '1 atrasada' : `${tareas.atrasadas} atrasadas`}</span>}
              </p>
            ) : <Vacio>No tiene tareas activas.</Vacio>}
          </Seccion>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="exp-raiz">
      <motion.div
        className="exp-fondo"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <motion.aside
        className="exp-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Expediente del empleado"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      >
        <div className="exp-barra">
          {pila.length > 1 ? (
            <button type="button" className="exp-barra-btn" onClick={volver}>
              <FaArrowLeft /> Volver
            </button>
          ) : <span className="exp-barra-titulo">Expediente</span>}
          <button type="button" className="exp-barra-btn" onClick={onClose} aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>
        {contenido()}
      </motion.aside>
    </div>,
    document.body
  );
}

export default ExpedienteEmpleado;
